// NEW_FILE_JUSTIFIED: Searchable company select for policy form — follows CountryCombobox pattern, no existing equivalent
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { ICompanyComboboxProps } from '@/@types/insurance';

export function CompanyCombobox({ companies, value, onChange, disabled }: ICompanyComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedName = companies.find(c => c._id === value)?.name;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls="company-combobox-list"
          disabled={disabled}
          className={cn(
            'w-full !h-10 justify-between font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          {selectedName || 'Select company'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search company..." />
          <CommandList id="company-combobox-list">
            <CommandEmpty>No company found.</CommandEmpty>
            <CommandGroup>
              {companies.map(company => (
                <CommandItem
                  key={company._id}
                  value={company.name}
                  className="data-[selected=true]:text-white"
                  onSelect={() => {
                    onChange(company._id === value ? '' : company._id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === company._id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {company.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
